import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, addDoc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { BILLING } from "../config/billing";
import { isEmulatorMode } from "./evalMode.js";

type CreateCheckoutOptions = {
  /** Stripe Promotion Code object ID (must start with `promo_`). The Firebase extension maps this to Checkout `discounts`; nested `discounts` on the Firestore doc are ignored. */
  promotionCodeId?: string | null;
  successUrl?: string;
  cancelUrl?: string;
};

/** Stripe Checkout only accepts the Promotion Code resource id (`promo_...`), not the customer-facing code string. */
function stripePromotionCodeIdOrUndefined(
  raw: string | null | undefined
): string | undefined {
  const id = raw?.trim();
  if (!id) return undefined;
  if (id.startsWith("promo_")) return id;
  console.warn(
    "[Stripe] Ignoring invalid welcome promotion id: expected a Promotion Code object id starting with `promo_` (Stripe Dashboard → Billing → Promotion codes → open your code → copy the id). Got:",
    id.slice(0, 12) + (id.length > 12 ? "…" : "")
  );
  return undefined;
}

export const createCheckoutSession = async (
  priceId: string,
  userId: string,
  trialLength: number,
  previousTrialed?: boolean,
  options?: CreateCheckoutOptions,
  promotionCodeId?: string
) => {
  if (isEmulatorMode) {
    // Eval stack: Stripe is disabled and there is no payments extension to attach a session.
    console.warn("[Stripe] checkout disabled in emulator/eval mode");
    return;
  }
  try {
    console.log("promotionCodeId", promotionCodeId);
    // Create the checkout session document
    const checkoutSessionRef = collection(
      db,
      "users",
      userId,
      "checkout_sessions"
    );

    const promoId = process.env.REACT_APP_STRIPE_WELCOME_PROMOTION_CODE_ID;
    const parsedTrial = Number(trialLength);
    const trialDays =
      Number.isFinite(parsedTrial) && parsedTrial > 0
        ? parsedTrial
        : BILLING.trialDays;
    const defaultReturn = `${window.location.origin}/my-designs`;
    // Fields must match what `ext-firestore-stripe-payments` reads from the doc (see checkout-session-creation handler).
    const sessionData: Record<string, unknown> = {
      mode: "subscription",
      success_url: options?.successUrl || defaultReturn,
      cancel_url: options?.cancelUrl || defaultReturn,
      promotion_code: promoId,
      trial_period_days: previousTrialed ? 0 : trialDays,
      line_items: [
        {
          price: priceId,
          quantity: 1,
        },
      ],

      collect_shipping_address: false,
      metadata: {
        userId: userId,
      },
    };
    sessionData.allow_promotion_codes = true;

    if (promoId) {
      sessionData.promotion_code = promoId;
    }
    console.log("[Stripe] sessionData:", JSON.stringify(sessionData, null, 2));

    // Create the checkout session
    const docRef = await addDoc(checkoutSessionRef, sessionData);

    // Wait for the CheckoutSession to get attached by the extension
    onSnapshot(docRef, (snap) => {
      const { error, url } = snap.data() as {
        error?: { message: string };
        url?: string;
      };
      if (error) {
        // Show an error to your customer and
        // inspect your Cloud Function logs in the Firebase console.
        console.log(error);
        alert(`An error occurred: ${error.message}`);
      }
      if (url) {
        // We have a Stripe Checkout URL, let's redirect.
        window.location.assign(url);
      }
    });
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

export const createPortalSession = async () => {
  if (isEmulatorMode) {
    console.warn("[Stripe] billing portal disabled in emulator/eval mode");
    return;
  }
  try {
    const functions = getFunctions();
    const functionRef = httpsCallable(
      functions,
      "ext-firestore-stripe-payments-ojuh-createPortalLink"
    );

    const { data } = await functionRef({
      returnUrl: window.location.origin + "/pricing",
    });

    // Redirect to the customer portal
    window.location.assign((data as { url: string }).url);
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};
